import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { QueryParams } from '@/lib/api';
import type { AuthMode, Empfehlung } from '@/types/api';

/**
 * Zugangsmigration (GET /systems/zugangsmigration).
 *
 * Der Yubikey-Schlüssel ERSETZT das Tresor-Passwort nicht - er kommt als
 * zweiter Weg dazu. Die Antwort zeigt deshalb je System beide Wege
 * nebeneinander, und der Fortschritt zählt Schlüssel, nicht Tresor-Ausstiege.
 *
 * AuthMode und Empfehlung kommen aus den generierten Typen (api.gen.ts): es
 * sind Enums des Vertrags, und eine zweite Liste hier wäre genau die stille
 * Drift, gegen die openapi.yaml als Quelle der Wahrheit gebaut ist. Die
 * Objektformen stehen lokal, weil sie nur diese eine Seite bedienen.
 */

export type { AuthMode, Empfehlung } from '@/types/api';

export interface ZugangsmigrationSystem {
  system_id: string;
  customer_id: string;
  customer_slug: string;
  customer_name: string;
  hostname: string;
  os_family: string | null;
  linux: boolean;
  auth_mode: AuthMode;
  passwort_im_tresor: boolean;
  bw_item_kind: string | null;
  key_bestaetigt: boolean;
  key_verified_at: string | null;
  key_verified_by: string | null;
  letzter_befund: string | null;
  letzter_zugang_ok: boolean | null;
  letzter_zugang_weg: 'key' | 'passwort' | null;
  access_code: string | null;
  access_checked_at: string | null;
  empfehlung: Empfehlung;
  hinweis: string | null;
}

export interface ZugangsmigrationKunde {
  customer_id: string;
  slug: string;
  name: string;
  passwort: number;
  passwort_und_key: number;
  key: number;
  unbekannt: number;
  manuell: number;
  gesamt: number;
  mit_key: number;
  offen: number;
}

export interface ZugangsmigrationFortschritt {
  systeme_gesamt: number;
  linux_gesamt: number;
  passwort: number;
  passwort_und_key: number;
  key: number;
  unbekannt: number;
  manuell: number;
  mit_key: number;
  zu_erledigen: number;
  prozent: number;
  ohne_jeden_weg: number;
}

export interface ZugangsmigrationRolloutVorschlag {
  routine_slug: string;
  customer_slugs: string[];
  system_ids: string[];
  /**
   * Alle übrigen Systeme der genannten Kunden. Unverändert an
   * excludeSystemIds der Rollout-Anfrage weiterreichen - sonst nimmt
   * POST /runs/rollout jedes passende System dieser Kunden, auch die auf
   * 'manuell' gestellten.
   */
  exclude_system_ids: string[];
  anzahl_systeme: number;
}

export interface ZugangsmigrationRollout {
  keys_ausrollen: ZugangsmigrationRolloutVorschlag;
  key_pruefen: ZugangsmigrationRolloutVorschlag;
}

export interface ZugangsmigrationResponse {
  fortschritt: ZugangsmigrationFortschritt;
  kunden: ZugangsmigrationKunde[];
  systeme: ZugangsmigrationSystem[];
  rollout: ZugangsmigrationRollout;
}

export interface ZugangsmigrationFilter {
  customerSlug?: string;
  authMode?: AuthMode;
  /** false = auch Nicht-Linux. true/undefined = Server-Default (nur Linux). */
  nurLinux?: boolean;
}

export interface EntkoppelnRequest {
  system_ids: string[];
  grund: string;
}

export interface EntkoppeltSystem {
  system_id: string;
  hostname: string;
  bw_item_id_alt: string | null;
  auth_mode: AuthMode;
}

export interface EntkoppelnResponse {
  entkoppelt: number;
  systeme: EntkoppeltSystem[];
}

export const zugangsmigrationKeys = {
  all: ['zugangsmigration'] as const,
  list: (filter: ZugangsmigrationFilter) => ['zugangsmigration', 'list', filter] as const,
};

function filterToParams(filter: ZugangsmigrationFilter): QueryParams {
  const params: QueryParams = {};
  if (filter.customerSlug) params.customer_slug = filter.customerSlug;
  if (filter.authMode) params.auth_mode = filter.authMode;
  // Nur senden, wenn Nicht-Linux eingeschaltet ist; Default bleibt serverseitig true.
  if (filter.nurLinux === false) params.nur_linux = false;
  return params;
}

export function useZugangsmigration(
  filter: ZugangsmigrationFilter = {}
): ReturnType<typeof useQuery<ZugangsmigrationResponse>> {
  return useQuery<ZugangsmigrationResponse>({
    queryKey: zugangsmigrationKeys.list(filter),
    queryFn: () =>
      api.get<ZugangsmigrationResponse>('/systems/zugangsmigration', filterToParams(filter)),
    staleTime: 30_000,
  });
}

export function useEntkoppelnZugang(): ReturnType<
  typeof useMutation<EntkoppelnResponse, Error, EntkoppelnRequest>
> {
  const qc = useQueryClient();
  return useMutation<EntkoppelnResponse, Error, EntkoppelnRequest>({
    mutationFn: (body) =>
      api.post<EntkoppelnResponse, EntkoppelnRequest>(
        '/systems/zugangsmigration/entkoppeln',
        body
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: zugangsmigrationKeys.all });
    },
  });
}
